import { z } from 'zod';
import { SiteSchema } from '../site.js';

/**
 * Wire contracts for the M02 API perimeter (CLAUDE.md §10). Single source of truth:
 * the Worker validates responses against these, the web client infers its types from them.
 * Keep request/response shapes here — never redeclare them per-app.
 */

/** GET /api/health — liveness + build/version probe. Unauthenticated-safe (no data). */
export const HealthResponseSchema = z.object({
  status: z.literal('ok'),
  service: z.literal('strata-api'),
  ts: z.string().datetime(),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/** GET /api/sites → list. */
export const SiteListResponseSchema = z.object({
  sites: z.array(SiteSchema),
});
export type SiteListResponse = z.infer<typeof SiteListResponseSchema>;

/** GET /api/sites/:id → single. */
export const SiteResponseSchema = z.object({
  site: SiteSchema,
});
export type SiteResponse = z.infer<typeof SiteResponseSchema>;

/**
 * PATCH /api/sites/:id input. M02 ships this route as a stubbed mutation that proves the
 * RBAC default-deny + audit path; the editable fields are intentionally minimal for now.
 */
export const SitePatchInputSchema = z
  .object({
    name: z.string().min(1).optional(),
    timezone: z.string().min(1).optional(),
    lat: z.number().min(-90).max(90).nullable().optional(),
    lng: z.number().min(-180).max(180).nullable().optional(),
  })
  .strict();
export type SitePatchInput = z.infer<typeof SitePatchInputSchema>;

/** Uniform error envelope returned by every Worker route on non-2xx. */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
